import React, { Component } from "react";
import axios from "axios";
import { awsAcordUpload } from "../../config";
import SmallLoader from "./smallLoader";
import moment from "moment";
import { quesList } from "../../utils/ques_list";
import { isExistingBusiness } from "../../utils/fieldAccess";

/**
 * ACORD Field Validators - Restrict data based on field type
 */
const acordValidators = {
  // Validate and clean alphabetic fields (names, cities, states)
  validateAlphabets: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const alphabetRegex = /^[a-zA-Z0-9-\s]+$/;
    const trimmedValue = value.trim();
    const isValid = alphabetRegex.test(trimmedValue);
    // Remove non-alphabetic characters if invalid
    const cleanedValue = isValid
      ? trimmedValue
      : trimmedValue.replace(/[^a-zA-Z\s]/g, "").trim();
    return { isValid, cleanedValue, originalValue: trimmedValue };
  },

  // Validate and clean numeric fields (FEIN, years, payroll)
  validateNumbers: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const numericValue = String(value).replace(/\D/g, "");
    const isValid = numericValue.length > 0 && !isNaN(numericValue);
    return { isValid, cleanedValue: numericValue, originalValue: value };
  },

  // Validate and clean zip codes
  validateZipCode: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const numericValue = String(value).replace(/\D/g, "");
    const isValid = numericValue.length === 5;
    return { isValid, cleanedValue: numericValue, originalValue: value };
  },

  // Validate and clean phone numbers
  validatePhoneNumber: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const numericValue = String(value).replace(/\D/g, "");
    const isValid = numericValue.length >= 10;
    return { isValid, cleanedValue: numericValue, originalValue: value };
  },

  // Validate email format
  validateEmail: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const isValid = emailRegex.test(value.toLowerCase());
    return { isValid, cleanedValue: value.toLowerCase(), originalValue: value };
  },

  // Validate alphanumeric (address, description)
  validateAlphanumeric: (value) => {
    if (!value) return { isValid: true, cleanedValue: "" };
    const alphanumericRegex = /^[a-zA-Z0-9\s\-.,#]*$/;
    const trimmedValue = value.trim();
    const isValid = alphanumericRegex.test(trimmedValue);
    const cleanedValue = isValid
      ? trimmedValue
      : trimmedValue.replace(/[^a-zA-Z0-9\s\-.,#]/g, "").trim();
    return { isValid, cleanedValue, originalValue: trimmedValue };
  },
};

// Field validation mapping for ACORD fields
const acordFieldValidationMap = {
  // Name fields - alphabets only
  companyName: { type: "alphabets", required: true },
  firstName: { type: "alphabets", required: false },
  lastName: { type: "alphabets", required: false },

  // Address fields
  street1: { type: "alphanumeric", required: false },
  street2: { type: "alphanumeric", required: false },
  cityName1: { type: "alphabets", required: false },
  cityName2: { type: "alphabets", required: false },

  // Numeric/Code fields
  fein: { type: "numbers", required: false },
  yearsInBusiness: { type: "numbers", required: false },
  zipCode1: { type: "zipCode", required: false },
  zipCode2: { type: "zipCode", required: false },

  // Phone/Email
  phoneNumber: { type: "phone", required: false },
  emailId: { type: "email", required: false },

  // Description
  descriptionOfOperations: { type: "alphanumeric", required: false },
};

class UploadFormAcord extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uploadingFile: false,
      awaitingFile: false,
      parseError: false,
      parseMesssage: "",
      urlSuccess: false,
      signedUrl: "",
      validationErrors: {}, // Track validation errors from ACORD
    };
  }

  /**
   * Validate ACORD field based on its type
   * @param {string} fieldName - Name of the field
   * @param {any} value - Value to validate
   * @returns {object} - { isValid, cleanedValue, errorMsg }
   */
  validateAcordField = (fieldName, value) => {
    debugger;
    if (!value) {
      return { isValid: true, cleanedValue: "", errorMsg: "" };
    }

    const validation = acordFieldValidationMap[fieldName];
    if (!validation) {
      return { isValid: true, cleanedValue: value, errorMsg: "" };
    }

    let validatorName = "";
    switch (validation.type) {
      case "alphabets":
        validatorName = "validateAlphabets";
        break;
      case "numbers":
        validatorName = "validateNumbers";
        break;
      case "zipCode":
        validatorName = "validateZipCode";
        break;
      case "phone":
        validatorName = "validatePhoneNumber";
        break;
      case "email":
        validatorName = "validateEmail";
        break;
      case "alphanumeric":
        validatorName = "validateAlphanumeric";
        break;
      default:
        return { isValid: true, cleanedValue: value, errorMsg: "" };
    }

    const validator = acordValidators[validatorName];
    if (!validator) {
      return { isValid: true, cleanedValue: value, errorMsg: "" };
    }

    const result = validator(value);

    // Build error message if validation failed
    let errorMsg = "";
    if (!result.isValid) {
      switch (validation.type) {
        case "alphabets":
          errorMsg = `${fieldName} contains invalid characters (only letters allowed)`;
          break;
        case "numbers":
          errorMsg = `${fieldName} contains invalid characters (only numbers allowed)`;
          break;
        case "zipCode":
          errorMsg = `${fieldName} must be exactly 5 digits`;
          break;
        case "phone":
          errorMsg = `${fieldName} must have at least 10 digits`;
          break;
        case "email":
          errorMsg = `${fieldName} has invalid email format`;
          break;
        case "alphanumeric":
          errorMsg = `${fieldName} contains invalid characters`;
          break;
        default:
          errorMsg = `${fieldName} validation failed`;
      }
    }

    return {
      isValid: result.isValid,
      cleanedValue: result.cleanedValue,
      originalValue: result.originalValue,
      errorMsg,
    };
  };

  //Log ACORD validation warnings to console and state
  addValidationWarning = (fieldName, warning) => {
    this.setState((prevState) => ({
      validationErrors: {
        ...prevState.validationErrors,
        [fieldName]: warning,
      },
    }));
    console.warn(`[ACORD Validation] ${fieldName}: ${warning}`);
  };

  createFormFieldData = (data) => {
    // remove the default responses from the first 24 acord questions
    let uw_questions = quesList;
    try {
      for (let i = 0; i < 24; i++) {
        delete uw_questions[i].response;
      }
    } catch (error) {}

    try {
      let street2, state2, zipCode2, city2;
      try {
        if (data?.basic_information?.mailing_address?.location_details) {
          street2 =
            data.basic_information.mailing_address.location_details?.address ||
            "";
          state2 =
            data.basic_information.mailing_address.location_details?.state ||
            "";
          zipCode2 =
            data.basic_information.mailing_address.location_details?.pincode ||
            "";
          city2 =
            data.basic_information.mailing_address.location_details?.city || "";
        } else if (data?.basic_information?.mailing_address?.address) {
          street2 = data.basic_information.mailing_address.address;
        }
      } catch (error) {}

      let street1, state1, zipCode1, city1;
      try {
        if (data?.basic_information?.primary_address?.location_details) {
          street1 =
            data.basic_information.primary_address.location_details?.address ||
            "";
          state1 =
            data.basic_information.primary_address.location_details?.state ||
            "";
          zipCode1 =
            data.basic_information.primary_address.location_details?.pincode ||
            "";
          city1 =
            data.basic_information.primary_address.location_details?.city || "";
        } else if (data?.basic_information?.primary_address?.address) {
          street1 = data.basic_information.primary_address.address;
        }
      } catch (error) {}

      let companyProfile = {};

      // Validate and set lastName
      const lastNameValidation = this.validateAcordField(
        "lastName",
        data.basic_information.contact_information?.last_name,
      );

      companyProfile.lastName = {
        value: lastNameValidation.cleanedValue,
        ...(lastNameValidation.errorMsg && {
          error: true,
          errMsg: lastNameValidation.errorMsg,
        }),
      };
      if (lastNameValidation.errorMsg) {
        this.addValidationWarning("lastName", lastNameValidation.errorMsg);
      }

      // Validate and set firstName
      const firstNameValidation = this.validateAcordField(
        "firstName",
        data.basic_information.contact_information?.first_name,
      );
      companyProfile.firstName = {
        value: firstNameValidation.cleanedValue,
        ...(firstNameValidation.errorMsg && {
          error: true,
          errMsg: firstNameValidation.errorMsg,
        }),
      };
      if (firstNameValidation.errorMsg) {
        this.addValidationWarning("firstName", firstNameValidation.errorMsg);
      }

      // Validate and set companyName
      const companyNameValidation = this.validateAcordField(
        "companyName",
        data.basic_information.company_name,
      );
      companyProfile.companyName = {
        value: companyNameValidation.cleanedValue,
        ...(companyNameValidation.errorMsg && {
          error: true,
          errMsg: companyNameValidation.errorMsg,
        }),
      };
      if (companyNameValidation.errorMsg) {
        this.addValidationWarning(
          "companyName",
          companyNameValidation.errorMsg,
        );
      }

      // Validate and set FEIN
      const feinValidation = this.validateAcordField(
        "fein",
        data.basic_information.fein,
      );
      companyProfile.fein = {
        value: feinValidation.cleanedValue,
        ...(feinValidation.errorMsg && {
          error: true,
          errMsg: feinValidation.errorMsg,
        }),
      };
      if (feinValidation.errorMsg) {
        this.addValidationWarning("fein", feinValidation.errorMsg);
      }

      // Validate and set yearsInBusiness
      const yearsValidation = this.validateAcordField(
        "yearsInBusiness",
        data.basic_information.years_in_business,
      );
      companyProfile.yearsInBusiness = {
        value: yearsValidation.cleanedValue,
        ...(yearsValidation.errorMsg && {
          error: true,
          errMsg: yearsValidation.errorMsg,
        }),
      };
      if (yearsValidation.errorMsg) {
        this.addValidationWarning("yearsInBusiness", yearsValidation.errorMsg);
      }

      // Validate and set phoneNumber
      const phoneValidation = this.validateAcordField(
        "phoneNumber",
        data.basic_information.phone_number ||
          data.basic_information.mobile_number,
      );
      companyProfile.phoneNumber = {
        value: phoneValidation.cleanedValue,
        ...(phoneValidation.errorMsg && {
          error: true,
          errMsg: phoneValidation.errorMsg,
        }),
      };
      if (phoneValidation.errorMsg) {
        this.addValidationWarning("phoneNumber", phoneValidation.errorMsg);
      }

      // Validate and set description
      const descValidation = this.validateAcordField(
        "descriptionOfOperations",
        data.basic_information.desc,
      );
      companyProfile.descriptionOfOperations = {
        value: descValidation.cleanedValue,
        ...(descValidation.errorMsg && {
          error: true,
          errMsg: descValidation.errorMsg,
        }),
      };
      if (descValidation.errorMsg) {
        this.addValidationWarning(
          "descriptionOfOperations",
          descValidation.errorMsg,
        );
      }

      // Validate and set street1 (Address - Primary)
      const street1Validation = this.validateAcordField("street1", street1);
      companyProfile.street1 = {
        value: street1Validation.cleanedValue,
        ...(street1Validation.errorMsg && {
          error: true,
          errMsg: street1Validation.errorMsg,
        }),
      };
      if (street1Validation.errorMsg) {
        this.addValidationWarning("street1", street1Validation.errorMsg);
      }

      // Validate and set cityName1
      const city1Validation = this.validateAcordField("cityName1", city1);
      companyProfile.cityName1 = {
        value: city1Validation.cleanedValue,
        ...(city1Validation.errorMsg && {
          error: true,
          errMsg: city1Validation.errorMsg,
        }),
      };
      if (city1Validation.errorMsg) {
        this.addValidationWarning("cityName1", city1Validation.errorMsg);
      }

      // Validate and set zipCode1
      const zipCode1Validation = this.validateAcordField("zipCode1", zipCode1);
      companyProfile.zipCode1 = {
        value: zipCode1Validation.cleanedValue,
        ...(zipCode1Validation.errorMsg && {
          error: true,
          errMsg: zipCode1Validation.errorMsg,
        }),
      };
      if (zipCode1Validation.errorMsg) {
        this.addValidationWarning("zipCode1", zipCode1Validation.errorMsg);
      }

      // Validate and set street2 (Address - Mailing)
      const street2Validation = this.validateAcordField("street2", street2);
      companyProfile.street2 = {
        value: street2Validation.cleanedValue,
        ...(street2Validation.errorMsg && {
          error: true,
          errMsg: street2Validation.errorMsg,
        }),
      };
      if (street2Validation.errorMsg) {
        this.addValidationWarning("street2", street2Validation.errorMsg);
      }

      // Validate and set cityName2
      const city2Validation = this.validateAcordField("cityName2", city2);
      companyProfile.cityName2 = {
        value: city2Validation.cleanedValue,
        ...(city2Validation.errorMsg && {
          error: true,
          errMsg: city2Validation.errorMsg,
        }),
      };
      if (city2Validation.errorMsg) {
        this.addValidationWarning("cityName2", city2Validation.errorMsg);
      }

      // Validate and set zipCode2
      const zipCode2Validation = this.validateAcordField("zipCode2", zipCode2);
      companyProfile.zipCode2 = {
        value: zipCode2Validation.cleanedValue,
        ...(zipCode2Validation.errorMsg && {
          error: true,
          errMsg: zipCode2Validation.errorMsg,
        }),
      };
      if (zipCode2Validation.errorMsg) {
        this.addValidationWarning("zipCode2", zipCode2Validation.errorMsg);
      }

      // Set states (states are passed as-is, converted to lowercase)
      companyProfile.state1 = { value: state1?.toLowerCase() || "" };
      companyProfile.state2 = { value: state2?.toLowerCase() || "" };

      // Set dates and other non-validated fields
      companyProfile.entityType = { value: "" };
      companyProfile.expectedStartDate = {
        value: new moment(data.basic_information.eff_date || ""),
      };
      companyProfile.effectiveDate = {
        value: new moment(data.basic_information.eff_date || ""),
      };

      // prepare children loc
      let locationClassCodeCurrentIndex = {};
      let childrenLoc = {};

      for (let ccIndex in data.state_details) {
        let state = data.state_details[ccIndex].location_details?.state;

        let locNumber;
        try {
          locNumber =
            String(Number(data.state_details[ccIndex].LOC) - 1) ||
            Number(ccIndex) + 1;
        } catch (error) {
          locNumber = data.state_details[ccIndex].LOC || Number(ccIndex) + 1;
        }
        let payroll;

        try {
          let remuneration = data.state_details[ccIndex].REMUNERATION;

          let isNumber = !isNaN(
            remuneration?.trim()?.replace("$", "")?.replace(/,/g, ""),
          );
          if (isNumber) {
            payroll = remuneration?.trim()?.replace("$", "");
          }
        } catch (error) {
          console.log(error);
        }

        // initialize/update the current index for the class code for this location
        if (!(locNumber in locationClassCodeCurrentIndex)) {
          locationClassCodeCurrentIndex[locNumber] = -1;
        }
        locationClassCodeCurrentIndex[locNumber]++;

        if (!childrenLoc[locNumber]) {
          let cityName = "";
          let street = "";
          try {
            cityName = data.state_details[ccIndex].location_details?.city;
            street = data.state_details[ccIndex].location_details?.address;
          } catch (error) {}

          // Validate location city name
          const locCityValidation = this.validateAcordField(
            `cityName_loc${locNumber}`,
            cityName,
          );

          // Validate location street
          const locStreetValidation = this.validateAcordField(
            `street_loc${locNumber}`,
            street,
          );

          // Validate location zip code
          const locZipValidation = this.validateAcordField(
            `zipCode_loc${locNumber}`,
            data.state_details[ccIndex].location_details?.pincode,
          );

          childrenLoc[locNumber] = {
            number: locNumber,
            zipCode: {
              value: locZipValidation.cleanedValue,
              ...(locZipValidation.errorMsg && {
                error: true,
                errMsg: locZipValidation.errorMsg,
              }),
            },
            cityName: {
              value: locCityValidation.cleanedValue,
              ...(locCityValidation.errorMsg && {
                error: true,
                errMsg: locCityValidation.errorMsg,
              }),
            },
            street: {
              value: locStreetValidation.cleanedValue,
              ...(locStreetValidation.errorMsg && {
                error: true,
                errMsg: locStreetValidation.errorMsg,
              }),
            },
            state: {
              value: state?.toLowerCase() || "",
            },
            classCodesInfo: { 0: {} },
          };

          // Log validation warnings for location fields
          if (locCityValidation.errorMsg) {
            this.addValidationWarning(
              `Location ${locNumber} City`,
              locCityValidation.errorMsg,
            );
          }
          if (locStreetValidation.errorMsg) {
            this.addValidationWarning(
              `Location ${locNumber} Street`,
              locStreetValidation.errorMsg,
            );
          }
          if (locZipValidation.errorMsg) {
            this.addValidationWarning(
              `Location ${locNumber} Zip Code`,
              locZipValidation.errorMsg,
            );
          }
        }

        // Validate payroll (numeric)
        let payrollValue = "";
        if (payroll) {
          const payrollValidation = this.validateAcordField("payroll", payroll);
          payrollValue = payrollValidation.cleanedValue;
          if (payrollValidation.errorMsg) {
            this.addValidationWarning(
              `Location ${locNumber} Payroll`,
              payrollValidation.errorMsg,
            );
          }
        }

        // Validate FT/PT (numeric employees)
        const ftValidation = this.validateAcordField(
          "ft",
          data.state_details[ccIndex].FULL,
        );
        const ptValidation = this.validateAcordField(
          "pt",
          data.state_details[ccIndex].PART,
        );

        if (ftValidation.errorMsg) {
          this.addValidationWarning(
            `Location ${locNumber} Full-Time Employees`,
            ftValidation.errorMsg,
          );
        }
        if (ptValidation.errorMsg) {
          this.addValidationWarning(
            `Location ${locNumber} Part-Time Employees`,
            ptValidation.errorMsg,
          );
        }

        childrenLoc[locNumber].classCodesInfo[
          locationClassCodeCurrentIndex[locNumber]
        ] = {
          classCodeDescription: {
            error: false,
            value:
              data.state_details[ccIndex].CLASS +
                ": " +
                data.state_details[ccIndex].DESCR || " ",
          },
          payroll: {
            value: payrollValue ? "$" + payrollValue : "$ ",
          },
          pt: {
            value:
              ptValidation.cleanedValue ||
              data.state_details[ccIndex].PART ||
              "",
          },
          ft: {
            value:
              ftValidation.cleanedValue ||
              data.state_details[ccIndex].FULL ||
              "",
          },
          fromAcord: true,
        };
      }

      let result = {
        companyProfile,
        childrenLoc,
      };

      if (data?.uw_questions?.length) {
        for (let localIndex in data.uw_questions) {
          let question = data.uw_questions[localIndex];
          let response =
            question?.response?.includes("Y") || question?.yes
              ? true
              : question?.response?.includes("N") || question?.no
                ? false
                : undefined;
          if (response !== undefined) {
            uw_questions[localIndex].response = response;
          }
        }
        sessionStorage.setItem("acordUwQues", JSON.stringify(uw_questions));
      }

      // prepare historical claims
      try {
        if (data?.historical_claims) {
          let acordHistoricalClaims = data.historical_claims;
          sessionStorage.setItem(
            "acordHistoricalClaims",
            JSON.stringify(acordHistoricalClaims),
          );
        }
      } catch (error) {}

      console.log(result);
      this.props.updateFormFields(result);
    } catch (error) {
      console.log("error while parsing acord results", error);
    }
  };

  handleLossOcrParse = (data) => {
    this.setState({ awaitingFile: false, disableBind: false });
    if (data.fileType === "success") {
      this.setState({
        parseMesssage: "Parsing complete!",
        parseError: false,
        urlSuccess: true,
        signedUrl: data.url,
      });
      this.createFormFieldData(data.data);
    } else if (data.fileType === "error") {
      this.setState({
        parseMesssage:
          "Unable to process the loss file. Please contact admin@insurecomp.com for further clarification.",
        parseError: true,
      });
    } else {
      this.setState({
        parseMesssage:
          "Unable to generate the download link. Please contact admin@insurecomp.com for further clarification.",
        parseError: true,
      });
    }
  };

  s3Poller = (fileName) => {
    axios
      .get(
        awsAcordUpload +
          `/api/s3PollerJson/ocr-doc-demo-acord/${fileName}/us-east-1`,
      )
      .then((result) => {
        if (result.data.fileType) {
          console.log("here in");
          this.handleLossOcrParse(result.data);
        } else {
          this.s3Poller(fileName);
        }
      })
      .catch((err) => {
        console.log("Error", err);
        this.setState({
          parseError: true,
          awaitingFile: false,
          parseMesssage:
            "Unable to process the loss file. Please contact admin@insurecomp.com for further clarification.",
        });
      });
  };

  handleFileUpload = (event) => {
    this.setState({ file: event.target.files });
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        this.setState({
          parseError: true,
          parseMesssage: "Wrong format selected. Select a PDF file.",
        });
        return;
      } else {
        this.setState({
          parseError: false,
        });
      }

      // Process the file upload here
      console.log("File selected:", file);
    }
  };

  uploadFile = (event) => {
    this.props.continueErr();
    // Check if a file is selected
    if (!this.state.file || this.state.file.length === 0) {
      // Set state to indicate error

      this.setState({
        parseError: true,
        parseMesssage: "Please upload a file.",
      });
      return; // Exit the function
    }

    // Reset any previous error state
    this.setState({
      uploadingFile: true,
      parseError: false,
      error: false,
      urlSuccess: false,
      signedUrl: "",
      awaitingFile: false,
    });

    const that = this;
    event.preventDefault();

    const formData = new FormData();
    formData.append("file", this.state.file[0]);
    let mode = process.env.REACT_APP_MODE;
    formData.append("client", "ies");
    formData.append("environment", mode);

    axios
      .post(awsAcordUpload + "/api/fileUploadAcord", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        that.setState({ uploadingFile: false });
        that.setState({ awaitingFile: true });
        const fileName = response.data.fileName;
        const folder = fileName.split(".")[0];
        console.log("file uploaded:", folder);
        this.s3Poller(folder);
      })
      .catch((error) => {
        this.setState({ uploadingFile: false });
        console.log("error", error);
      });
  };

  render() {
    const {
      uploadingFile,
      awaitingFile,
      urlSuccess,
      signedUrl,
      parseError,
      parseMesssage,
      validationErrors,
    } = this.state;

    let mobile_width = window.innerWidth <= 500 ? { width: "220px" } : {};
    const isDisabled = isExistingBusiness();

    // Check if there are any validation errors
    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    return (
      <div className="acordParseform">
        <p>Select an acord document for analysis</p>
        <form className="">
          <div className="d-flex">
            <div className="form-group">
              <input
                disabled={isDisabled}
                label="upload file"
                type="file"
                onChange={this.handleFileUpload}
                className="acord_input"
                style={mobile_width}
                accept=".pdf"
              />
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary acordSubmitButton"
                onClick={this.uploadFile}
                disabled={parseError || isDisabled}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
        <div className="">
          {uploadingFile && (
            <span>
              Uploading file <SmallLoader />
            </span>
          )}
          {awaitingFile && (
            <span>
              Waiting for file creation <SmallLoader />
            </span>
          )}
          {urlSuccess && (
            <div>
              <a href={signedUrl}>
                {parseMesssage} Click here to download your file.
              </a>
            </div>
          )}
          {parseError && (
            <div style={{ color: "red", marginTop: "0.5rem" }}>
              {parseMesssage}
            </div>
          )}
          {hasValidationErrors && (
            <div
              style={{
                color: "#ff9800",
                marginTop: "1rem",
                padding: "0.75rem",
                backgroundColor: "#fff3e0",
                borderLeft: "4px solid #ff9800",
                borderRadius: "4px",
              }}
            >
              <strong>⚠️ ACORD Data Validation Warnings:</strong>
              <ul
                style={{
                  marginTop: "0.5rem",
                  marginBottom: 0,
                  paddingLeft: "1.5rem",
                }}
              >
                {Object.entries(validationErrors).map(([field, warning]) => (
                  <li
                    key={field}
                    style={{ fontSize: "0.9rem", marginBottom: "0.25rem" }}
                  >
                    <strong>{field}:</strong> {warning}
                  </li>
                ))}
              </ul>
              <p
                style={{
                  fontSize: "0.85rem",
                  marginTop: "0.5rem",
                  marginBottom: 0,
                }}
              >
                Some ACORD data contains invalid characters and has been
                cleaned. Please review the populated fields.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default UploadFormAcord;
